import { css } from "@emotion/react";
import styled from "@emotion/styled";

import {
  fontMerriweather,
  fontRobotoCondensed,
  setFullViewportWidthStyles,
  theme,
} from "shared/lib/styles";

import { GameType } from "scmp-app/components/games/types";

const getTitleStyle = (gameType: GameType) => {
  switch (gameType) {
    case GameType.Cryptic:
    case GameType.Quick:
      return css`
        font-size: 16px;
        font-family: ${fontMerriweather};
      `;
    case GameType.MiniSudoku:
    case GameType.WordSearch:
      return css`
        font-size: 18px;
        font-family: ${fontRobotoCondensed};
      `;
  }
};
export type TitleProps = {
  $gameType: GameType;
};
export const Title = styled.div<TitleProps>`
  display: inline-block;
  gap: 3px;

  color: #000000;
  font-weight: 700;
  font-style: normal;
  line-height: 140%;
  text-align: center;

  ${props => getTitleStyle(props.$gameType)};
`;

export const CrosswordName = styled.div`
  display: inline;

  font-weight: 400;
`;

type GifImageProps = {
  $theme: GameType;
};
export const GifImage = styled.img<GifImageProps>`
  inline-size: ${props => (props.$theme === GameType.WordSearch ? "88px" : "48px")};
  block-size: ${props => (props.$theme === GameType.WordSearch ? "18px" : "48px")};
`;

const getRowStyle = (gameType: GameType) => {
  switch (gameType) {
    case GameType.Cryptic:
    case GameType.Quick:
      return css`
        grid-gap: 2.667px;

        border: 2.667px solid #000000;

        background-color: #000000;

        ${theme.breakpoints.up("desktop")} {
          grid-gap: 3.733px;

          border-width: 3.733px;
        }
      `;
    case GameType.MiniSudoku:
      return css`
        gap: 7.09px;

        ${theme.breakpoints.up("tablet")} {
          gap: 9.93px;
        }
      `;
    case GameType.WordSearch:
      return css`
        gap: 7.09px;

        ${theme.breakpoints.up("desktop")} {
          gap: 9.45px;
        }
      `;
  }
};

export type RowProps = {
  $gameType: GameType;
};
export const Row = styled.div<RowProps>`
  display: grid;
  grid-template-columns: repeat(7, minmax(auto, auto));

  ${props => getRowStyle(props.$gameType)};
`;

const speedFactor = 3.5; // Higher value = faster animation

const generateNthOfTypeStyles = (duration: number) => {
  let styles = "";
  for (let index = 1; index <= 7; index++) {
    styles += `
      &:nth-of-type(${index}) {
        animation-delay: ${((index - 1) * duration) / speedFactor}s;
      }
    `;
  }
  return styles;
};

type CellProps = {
  $duration: number;
  $theme?: GameType;
};

const getCellStyle = (gameType: GameType) => {
  switch (gameType) {
    case GameType.Cryptic:
    case GameType.Quick:
      return css`
        inline-size: 40px;
        block-size: 40px;

        font-size: 32px;
        letter-spacing: -0.4px;

        background: #ffffff;

        ${theme.breakpoints.up("desktop")} {
          inline-size: 56px;
          block-size: 56px;

          font-size: 44.8px;
          letter-spacing: -0.56px;
        }
      `;
    case GameType.MiniSudoku:
      return css`
        inline-size: 36px;
        block-size: 40px;

        color: #e8e7e5;
        font-size: 21px;

        border: 1px solid #596d35;
        border-radius: 50%;

        background-color: #1c3017;

        ${theme.breakpoints.up("desktop")} {
          inline-size: 50.4px;
          block-size: 56px;

          font-size: 29.4px;
        }
      `;
    case GameType.WordSearch:
      return css`
        inline-size: 36px;
        block-size: 36px;

        color: #e8e7e5;
        font-size: 21px;

        border-radius: 50%;

        background: #000000;

        ${theme.breakpoints.up("desktop")} {
          inline-size: 48px;
          block-size: 48px;

          font-size: 28px;
        }
      `;
  }
};

export const Cell = styled.div<CellProps>`
  display: flex;
  justify-content: center;
  align-items: center;

  font-weight: 600;
  font-family: ${fontRobotoCondensed};
  font-style: normal;
  line-height: normal;
  text-transform: uppercase;

  animation: highlight-animation 3.5s infinite;
  ${props =>
    props.$duration ? `animation-duration: ${(props.$duration * 7) / speedFactor}s;` : ""}
  ${props => css`
    ${generateNthOfTypeStyles(props.$duration ?? 0)}
  `}

  ${props => getCellStyle(props.$theme ?? GameType.Quick)};

  @keyframes highlight-animation {
    0%,
    14.28% {
      background-color: ${props => {
        switch (props.$theme) {
          case GameType.Cryptic:
          case GameType.Quick:
            return "#ffffff";
          case GameType.MiniSudoku:
            return "#1C3017";
          case GameType.WordSearch:
            return "#000";
        }
      }};
    }

    14.29%,
    28.57% {
      background-color: ${props => {
        switch (props.$theme) {
          case GameType.Cryptic:
            return "#8fb6ff";
          case GameType.MiniSudoku:
            return "#E6FB7C";
          case GameType.Quick:
            return "#ffc500";
          case GameType.WordSearch:
            return "#FF7728";
        }
      }};
    }

    28.58%,
    100% {
      background-color: ${props => {
        switch (props.$theme) {
          case GameType.Cryptic:
          case GameType.Quick:
            return "#ffffff";
          case GameType.MiniSudoku:
            return "#1C3017";
          case GameType.WordSearch:
            return "#000";
        }
      }};
    }
  }
`;
export const AdContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  inline-size: 100%;
  block-size: 126px;
  padding-block-end: 10px;

  background-color: #f7f7f7;
`;

type ContentProps = {
  $theme?: string;
};

export const Content = styled.div<ContentProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  align-items: center;
`;

const getContainerThemeStyle = (theme?: GameType) => {
  switch (theme) {
    case GameType.Cryptic:
      return css`
        background: #1e2836;
        ${Title} {
          color: #ffffff;
        }
      `;
    case GameType.Quick:
      return css`
        background: #ffffff;
      `;
    case GameType.WordSearch:
      return css`
        background: #e8e2d7;
      `;
    default:
      return css`
        background: #ffffff;
      `;
  }
};

interface ContainerProps {
  $fadeOutDuration?: number;
  $theme?: GameType;
  $visible?: boolean;
}

export const Container = styled.div<ContainerProps>`
  ${setFullViewportWidthStyles()}
  position: absolute;
  inset-block-start: 0;
  z-index: 10;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  block-size: calc(100vh - 166px);

  opacity: ${props => (props.$visible ? 1 : 0)};

  pointer-events: ${props => (props.$visible ? "auto" : "none")};

  transition: opacity ${props => props.$fadeOutDuration ?? 500}ms ease-in-out;

  ${theme.breakpoints.up("tablet")} {
    block-size: calc(100vh - 179px);
  }
  ${theme.breakpoints.up("desktop")} {
    block-size: calc(100vh - 349px);
  }

  ${props => getContainerThemeStyle(props.$theme)}
`;
